import React, { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { 
  Search, MapPin, Calendar, Users,
  ChevronRight, Loader2, Inbox 
} from "lucide-react";

// ─── UI HELPERS ──────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
    "Pending": "bg-orange-50 text-orange-700 border-orange-200",
    "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Delayed": "bg-red-50 text-red-700 border-red-200"
  };
  const defaultStyle = "bg-slate-50 text-slate-700 border-slate-200";
  
  return (
    <span className={`text-[10px] font-black px-2.5 py-1 rounded-md border uppercase tracking-wider ${map[status] || defaultStyle}`}>
      {status}
    </span>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function DispatcherOrders() {
  const navigate = useNavigate();
  const { user } = useOutletContext() || {};

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All"); 
  const [searchQuery, setSearchQuery] = useState("");

  const isMounted = useRef(true);
  const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

  // ─── FETCH ASSIGNMENTS ─────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token) return;

    try {
      setLoading(true);
      
      // Your Django backend automatically filters this based on the user's token!
      // If user.role === 'dispatcher', it only returns loads assigned specifically to them.
      const res = await fetch(`${apiUrl}/api/loads/assignments/mine/`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!isMounted.current) return;

      if (res.ok) {
        const rawAssignments = await res.json();
        
        // Map backend format to UI expected format
        const mappedOrders = rawAssignments.map(a => {
          let uiStatus = "Pending";
          if (["started", "in_transit", "paused"].includes(a.status)) uiStatus = "In Progress";
          if (a.status === "completed") uiStatus = "Completed";
          if (a.status === "cancelled") uiStatus = "Delayed"; 

          return {
            id: a.load?.reference_code || `PLU-${a.id.substring(0,6)}`,
            rawId: a.id,
            status: uiStatus,
            origin: a.load?.origin_label || "Origin",
            destination: a.load?.destination_label || "Destination",
            driver: a.driver?.full_name || "Unassigned",
            driverInitials: a.driver?.first_name ? a.driver.first_name.charAt(0).toUpperCase() : "D",
            date: a.load?.departure_date || "TBD",
            commodity: a.load?.commodity || "Oversize Load",
            escorts: a.load?.escorts_required || 1,
            raw: a 
          };
        });
        
        setOrders(mappedOrders);
      }
    } catch (err) {
      console.error("Failed to fetch dispatcher orders:", err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    isMounted.current = true;
    fetchOrders();
    return () => { isMounted.current = false; };
  }, [fetchOrders]);

  // ─── FILTER & SEARCH LOGIC ─────────────────────────────────────────────────
  const filteredOrders = orders.filter(order => {
    const matchesTab = filter === "All" || order.status === filter;
    const matchesSearch = 
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.origin.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.destination.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.driver.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesTab && matchesSearch;
  });

  const counts = {
    All: orders.length,
    "In Progress": orders.filter(o => o.status === "In Progress").length,
    Pending: orders.filter(o => o.status === "Pending").length,
    Completed: orders.filter(o => o.status === "Completed").length,
  };

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      
      {/* Header Area */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900">Assigned Orders</h1>
          <p className="text-sm text-slate-500 font-medium mt-1">
            Viewing loads assigned specifically to you by {user?.company_name || "the company"}.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {/* Search Bar */}
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search ID, city, or driver..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200 overflow-x-auto custom-scrollbar">
            {["All", "In Progress", "Pending", "Completed"].map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 ${
                  filter === tab ? "bg-[#1a2744] text-white shadow" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {tab}
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${filter === tab ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>
                  {counts[tab]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
          <span className="text-sm font-semibold text-slate-400">Loading your assignments...</span>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-white">
          <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4 border border-slate-100">
            <Inbox className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-2">No orders found</h3>
          <p className="text-sm text-slate-500 max-w-sm leading-relaxed">
            {searchQuery 
              ? "No orders match your search criteria. Try adjusting your filters." 
              : "You haven't been assigned to any loads by the company yet."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredOrders.map((order) => (
            <div key={order.rawId} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all p-5 flex flex-col flex-shrink-0 group">
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-black text-slate-900">{order.id}</span>
                    <StatusPill status={order.status} />
                  </div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{order.commodity}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {order.date}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                    <Users className="w-3 h-3" /> {order.escorts} Escort{order.escorts > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="space-y-3 mb-5 flex-1">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5 border border-amber-100">
                    <MapPin className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pickup</p>
                    <p className="text-sm font-bold text-slate-900 truncate">{order.origin}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center shrink-0 mt-0.5 border border-teal-100">
                    <MapPin className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dropoff</p>
                    <p className="text-sm font-bold text-slate-900 truncate">{order.destination}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">
                    {order.driverInitials}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assigned Driver</p>
                    <p className="text-xs font-bold text-slate-800">{order.driver}</p>
                  </div>
                </div>
                
                {/* Navigates directly to the dispatcher live dashboard */}
                <button 
                  onClick={() => navigate("/dispatcher-dashboard")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-50 group-hover:bg-blue-600 text-slate-400 group-hover:text-white transition-colors border border-slate-200 group-hover:border-blue-600 shadow-sm"
                  title="Open in Live Console"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

            </div>
          ))}
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: #94a3b8; }
      `}} />
    </div>
  );
}